import React, { useState, useEffect } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Space, DatePicker, Select, Input, Form, Modal, InputNumber } from 'antd';
import { NotificationInstance } from 'antd/es/notification/interface';

import { DeleteOutlined, SaveOutlined, QuestionCircleOutlined, DeliveredProcedureOutlined } from '@ant-design/icons';

import { exception, error } from '@extensions/notification';

import { useAppDispatch } from '@store/hooks';
import { userLoaded } from '@store/actions';

import Toolbar from '@controls/toolbar/toolbar';
import PhoneInputFormItem from '@controls/phone-input-form-item/phone-input-form-item';

import IBaseComponentProps from '@components/base-component-props';
import TradingPlatform from '@controls/trading-platform/trading-platform';

import { IMarking } from '@entities/marking';
import { ICountryItem } from '@entities/country-item';
import { ICountry } from '@entities/country';
import { ITradingPlatform } from '@entities/trading-platform';
import { ILoadingPlan } from '@entities/loading-plan';
import { IUserSession } from '@entities/user-session';

import { LoadingPlanStatus, enumLabel } from '@enums/loading-plan-status';

dayjs.extend(utc);

interface ILoadingPlanProps extends IBaseComponentProps {
    planId: string | undefined;
    baseCountries: Array<ICountry>;
    userSession: IUserSession;
    api: NotificationInstance;
    onSave: () => void;
    onDelete: () => void;
    onCancel: () => void;
    onComplete: (plan: ILoadingPlan) => void;
}

const LoadingPlan = (props: ILoadingPlanProps) => {
    const { planId, baseCountries, userSession, api, onSave, onDelete, onCancel, onComplete, serverFetch } = props;

    const [form] = Form.useForm();
    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();

    const [markings, setMarkings] = useState<Array<IMarking>>([]);
    const [countryItems, setCountryItems] = useState<Array<ICountryItem>>([]);
    const [countries, setCountries] = useState<Array<ICountry>>([]);
    const [currentCountry, setCurrentCountry] = useState<ICountry>();
    const [viewMode, setViewMode] = useState<boolean>();

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            const promises = [
                await serverFetch(`countries/items`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения продуктов', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch(`markings`, { method: 'GET', queryParams: { userId: userSession.userId } })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения маркировок', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch(`tradingplatforms`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения торговых площадок', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            if (planId) {
                promises.push(
                    await serverFetch(`loadingplans/${planId}`, { method: 'GET' })
                        .then((data: any) => {
                            return data;
                        })
                        .catch((ex: any) => {
                            exception(api, 'Ошибка получения плана погрузки', ex, () => d(userLoaded(undefined)));
                        }),
                );
            }

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                loadPlatforms();

                setCountryItems(result[0][0]);
                setMarkings(result[0][1]);

                const platforms: Array<ITradingPlatform> = result[0][2];
                let tmpCountries = [...baseCountries];
                tmpCountries.map((c: ICountry) => {
                    let tmpPlatforms = platforms.filter((d: ITradingPlatform) => d.countryId == c.id);
                    c.platforms = [...tmpPlatforms];
                });
                setCountries(tmpCountries);

                let plan: ILoadingPlan | undefined = undefined;
                if (planId) {
                    plan = result[0][3];
                    setViewMode(plan?.status == LoadingPlanStatus.Processed);
                }

                const country = tmpCountries.find((c) => c.id == plan?.countryId);
                setCurrentCountry(country);

                form.setFieldsValue(plan);
                form.setFieldValue('status', plan?.status || LoadingPlanStatus.New);
                form.setFieldValue(
                    'plannedDate',
                    plan?.plannedDate && dayjs(plan.plannedDate).utc(true).set('hour', 0).set('minute', 0).set('second', 0),
                );
            });
        };

        fetchData();
        return () => {
            cleanup = true;
        };
    }, []);

    const loadPlatforms = () => {
        serverFetch(`tradingplatforms`, { method: 'GET' })
            .then((data: Array<ITradingPlatform>) => {
                let tmpCountries = [...baseCountries];

                tmpCountries.map((c: ICountry) => {
                    let tmpPlatforms = data.filter((d: ITradingPlatform) => d.countryId == c.id);
                    c.platforms = [...tmpPlatforms];
                });

                setCountries(tmpCountries);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения торговых площадок', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSavePlan = (data: ILoadingPlan) => {
        if (!data?.qty && !data?.grossWeight && !data?.volumeWeight) {
            error(api, `Пожалуйста, укажите количество или вес`);
            return;
        }

        serverFetch(`loadingplans`, { method: data.id ? 'PUT' : 'POST', bodyData: data })
            .then(() => {
                onSave();
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка сохранения плана', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Сохранить',
                        key: 'save',
                        disabled: viewMode,
                        icon: <SaveOutlined />,
                        onClick: () => {
                            form.submit();
                        },
                    },
                    {
                        label: 'Удалить',
                        key: 'delete',
                        disabled: viewMode,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            modal.confirm({
                                title: `Удалить план погрузки?`,
                                okType: 'primary',
                                icon: <QuestionCircleOutlined />,
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDelete();
                                },
                            });
                        },
                    },
                    {
                        label: 'Переместить в "Предзагрузку"',
                        key: 'complete',
                        disabled: viewMode,
                        type: 'primary',
                        icon: <DeliveredProcedureOutlined />,
                        onClick: () => {
                            modal.confirm({
                                title: `Переместить выбранный план в "Предзагрузку"?`,
                                okType: 'primary',
                                icon: <QuestionCircleOutlined />,
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    form.validateFields();

                                    const plan = form.getFieldsValue();
                                    if (!plan?.qty && !plan?.grossWeight && !plan?.volumeWeight) {
                                        error(api, `Пожалуйста, укажите количество или вес`);
                                        return;
                                    }

                                    onComplete(plan);
                                },
                            });
                        },
                    },
                ]}
            />
        );
    };

    return (
        <>
            <Modal
                destroyOnHidden={true}
                width={500}
                title={'План погрузки'}
                open={true}
                okText='ОК'
                okButtonProps={{ style: { display: 'none' } }}
                cancelText='Закрыть'
                onCancel={() => onCancel()}
            >
                <div style={{ marginTop: 20 }}>{renderToolbar()}</div>

                <Form
                    disabled={viewMode}
                    colon={false}
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    style={{ marginTop: 20 }}
                    form={form}
                    onFinish={(e) => onSavePlan(e)}
                >
                    <Form.Item name='id' hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item name='userId' hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item name='status' hidden>
                        <Input />
                    </Form.Item>

                    <Form.Item label='ФИО'>
                        <Input value={form.getFieldValue('fullName')} disabled={true} />
                    </Form.Item>
                    <PhoneInputFormItem value={form.getFieldValue('userPhone')} name='userPhone' label='Телефон' disabled={true} />
                    <Form.Item label='Статус'>
                        <Input value={enumLabel(form.getFieldValue('status'))} disabled={true} />
                    </Form.Item>

                    <Form.Item label='Маркировка' required name='markingId' rules={[{ required: true, message: 'Укажите маркировку' }]}>
                        <Select
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={markings.map((m) => {
                                return { value: m.id, label: `${m.code} / ${m.cityName}` };
                            })}
                        />
                    </Form.Item>
                    <Form.Item label='Страна' name='countryId' rules={[{ required: true, message: 'Укажите страну' }]}>
                        <Select
                            onChange={(value) => {
                                const country = countries.find((c) => c.id == value);
                                setCurrentCountry(country);

                                form.setFieldValue('itemId', undefined);
                                form.setFieldValue('awbNumber', undefined);
                            }}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().startsWith(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={countries.map((i) => {
                                return { value: i.id, label: i.name };
                            })}
                        />
                    </Form.Item>
                    <Form.Item label='Продукт' name='itemId' rules={[{ required: true, message: 'Укажите продукт' }]}>
                        <Select
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().startsWith(input.toLowerCase()),
                            }}
                            options={countryItems
                                .filter((i) => i.countryId == form.getFieldValue('countryId'))
                                .map((i) => {
                                    return { value: i.itemId, label: i.itemName };
                                })}
                        />
                    </Form.Item>
                    <Form.Item
                        label='Дата погрузки'
                        name='plannedDate'
                        rules={[{ required: true, message: 'Укажите планируемую дату погрузки' }]}
                    >
                        <DatePicker
                            format={'DD.MM.YYYY'}
                            onChange={(data) => {
                                form.setFieldValue(
                                    'plannedDate',
                                    data && dayjs(data).utc(true).set('hour', 0).set('minute', 0).set('second', 0),
                                );
                            }}
                        />
                    </Form.Item>

                    <Form.Item label={currentCountry?.code == 'NL' ? 'Торговая площадка' : 'AWB'} name='awbNumber'>
                        {currentCountry?.code == 'NL' ? (
                            <TradingPlatform
                                country={currentCountry}
                                basePlatforms={currentCountry?.platforms}
                                defaultPlatformName={form.getFieldValue('awbNumber')}
                                api={api}
                                onSelect={(value: string) => {
                                    form.setFieldValue('awbNumber', value);
                                }}
                                loadPlatforms={loadPlatforms}
                                serverFetch={serverFetch}
                            />
                        ) : (
                            <Input />
                        )}
                    </Form.Item>
                    <Form.Item label='Количество'>
                        <Space.Compact>
                            <Form.Item noStyle name='qty'>
                                <InputNumber min={0} />
                            </Form.Item>
                            <Space.Addon>шт</Space.Addon>
                        </Space.Compact>
                    </Form.Item>
                    <Form.Item label='Брутто'>
                        <Space.Compact>
                            <Form.Item noStyle name='grossWeight'>
                                <InputNumber decimalSeparator=',' min={0} stringMode step={1} />
                            </Form.Item>
                            <Space.Addon>кг</Space.Addon>
                        </Space.Compact>
                    </Form.Item>
                    <Form.Item label='Объемный'>
                        <Space.Compact>
                            <Form.Item noStyle name='volumeWeight'>
                                <InputNumber decimalSeparator=',' min={0} stringMode step={1} />
                            </Form.Item>
                            <Space.Addon>кг</Space.Addon>
                        </Space.Compact>
                    </Form.Item>
                </Form>
            </Modal>

            {modalContextHolder}
        </>
    );
};

export default LoadingPlan;
